import React, { useEffect, useState, useRef } from "react";

function Photography() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());
  const cacheRef = useRef(null);
  const cacheTimeout = 5 * 60 * 1000; // 5 minutes cache

  // Fallback local images for better performance (optimized sizes)
  const fallbackPhotos = [
    { name: "Sample Photo 1", url: "/Assets/Projects/blog.png", thumbnail: "/Assets/Projects/blog.png" },
    { name: "Sample Photo 2", url: "/Assets/Projects/chatify.png", thumbnail: "/Assets/Projects/chatify.png" },
    { name: "Sample Photo 3", url: "/Assets/Projects/codeEditor.png", thumbnail: "/Assets/Projects/codeEditor.png" },
    { name: "Sample Photo 4", url: "/Assets/Projects/emotion.png", thumbnail: "/Assets/Projects/emotion.png" },
    { name: "Sample Photo 5", url: "/Assets/Projects/leaf.png", thumbnail: "/Assets/Projects/leaf.png" },
    { name: "Sample Photo 6", url: "/Assets/Projects/suicide.png", thumbnail: "/Assets/Projects/suicide.png" }
  ];

  // Check if cache is valid
  const isCacheValid = () => {
    if (!cacheRef.current || !lastFetch) return false;
    const now = Date.now();
    return (now - lastFetch) < cacheTimeout;
  };

  async function loadPhotos(forceRefresh = false) {
    // Use cached data if available and valid
    if (!forceRefresh && isCacheValid() && cacheRef.current) {
      setPhotos(cacheRef.current);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const res = await fetch("/api/photos", { 
        signal: controller.signal 
      });
      clearTimeout(timeoutId);
      
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data?.items) && data.items.length > 0) {
          // Cache the successful result
          cacheRef.current = data.items;
          setLastFetch(Date.now());
          setPhotos(data.items);
        } else {
          cacheRef.current = fallbackPhotos;
          setLastFetch(Date.now());
          setPhotos(fallbackPhotos);
        }
      } else {
        cacheRef.current = fallbackPhotos;
        setLastFetch(Date.now());
        setPhotos(fallbackPhotos);
      }
    } catch (e) {
      // Use fallback images if API fails or times out
      cacheRef.current = fallbackPhotos;
      setLastFetch(Date.now());
      setPhotos(fallbackPhotos);
    } finally {
      setLoading(false);
    }
  }

  // Image optimization functions
  const handleImageError = (index) => {
    setImageLoadErrors(prev => new Set(prev).add(index));
  };

  const getOptimizedImageUrl = (photo, index) => {
    // If image failed to load, use a placeholder
    if (imageLoadErrors.has(index)) {
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='250' height='250' viewBox='0 0 250 250'%3E%3Crect width='250' height='250' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999' font-family='Arial' font-size='14'%3EImage not available%3C/text%3E%3C/svg%3E";
    }
    
    // Use thumbnail if available, otherwise use original URL
    return photo.thumbnail || photo.url;
  };

  useEffect(() => {
    loadPhotos();
  }, []);

  return (
    <section className="photography-section">
      <div className="container py-4">
        <h2 className="project-heading">My Photography</h2>
        
        {loading ? (
          <div className="photography-loading">
            <div className="loading-spinner"></div>
            <p>Loading photography gallery...</p>
          </div>
        ) : (
          <div className="photography-grid">
            {photos.map((p, idx) => (
              <div key={idx} className="photography-item">
                <img 
                  src={getOptimizedImageUrl(p, idx)}
                  alt={p.name || `photo-${idx}`} 
                  className="photography-image" 
                  loading="lazy"
                  onError={() => handleImageError(idx)}
                  onLoad={() => {
                    // Remove from error set if image loads successfully
                    if (imageLoadErrors.has(idx)) {
                      setImageLoadErrors(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(idx);
                        return newSet;
                      });
                    }
                  }}
                />
                <div className="photography-overlay">
                  <h4 className="photography-title">{p.name}</h4>
                </div>
              </div>
            ))}
            {photos.length === 0 && (
              <p>No photos found in the configured directory.</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default Photography;
